const messages: Record<string, string> = {
  // ── Chat errors ──────────────────────────────────────────────────────
  'chat.error.sessionExpired': 'Your session has expired',
  'chat.error.authenticationRequired': 'Authentication required',
  'chat.error.connectionFailed': 'Connection failed',
  'chat.error.requestFailed': 'Request failed',
  'chat.error.turnFailed':
    'The assistant could not finish this response. Support reference {trace}.',
  'chat.error.unknown': 'An unexpected error occurred',
  'chat.error.support': 'Please try again later or contact support if the problem persists.',
  'chat.error.conversationLimit':
    "You've reached the maximum number of active conversations. Please wait for one to finish, or cancel one before starting a new chat.",
  'chat.error.tryAgain': 'Please try again',
  'chat.error.fileTooLarge': '"{name}" exceeds the 500 MB limit and cannot be uploaded.',
  'chat.error.uploadSizeExceeded': 'File too large. Maximum upload size is 500 MB.',
  'chat.error.imageUploadBlocked': 'This image could not be uploaded.',
  'chat.error.tooManyAttachments':
    'A message can carry at most {max, plural, one {# attachment} other {# attachments}}.',

  'chat.interrupted.message':
    'Sorry, something interrupted this response. Click Try again to resend your message.',
  'chat.interrupted.cancelled': 'You stopped this response.',
  'chat.interrupted.noReply': 'The assistant finished without a written reply.',
  'chat.interrupted.creditsExhausted': 'You ran out of credits before this response finished.',
  'chat.interrupted.cutShort': 'This response was cut short before it finished.',
  'chat.interrupted.retry': 'Try again',
  'chat.interrupted.askForSummary': 'Ask for a summary',
  'chat.interrupted.summaryPrompt': 'Please summarise what you did and the result.',

  // ── Attachments ───────────────────────────────────────────────────
  'attachments.remove': 'Remove {name}',
  'attachments.previewUnavailable': 'Preview unavailable',
  'attachments.pdf.loading': 'Loading…',
  'attachments.pdf.unreadable':
    'This PDF could not be displayed. Download it to open it locally.',
  'attachments.pdf.pageOf': '{page} / {total}',
  'attachments.pdf.previousPage': 'Previous page',
  'attachments.pdf.nextPage': 'Next page',
  'attachments.pdf.zoomIn': 'Zoom in',
  'attachments.pdf.zoomOut': 'Zoom out',
  'attachments.pdf.download': 'Download',
  'attachments.pdf.close': 'Close preview',
  'attachments.pdf.open': 'Open {name}',
  'attachments.pdf.load': 'Load PDF preview ({size})',
  'attachments.pdf.retry': 'Retry',
  'attachments.pdf.pageCount': '{count, plural, one {# page} other {# pages}}',
  'htmlArtifact.view': 'View',
  'htmlArtifact.preview': 'Preview',
  'htmlArtifact.code': 'Code',
  'htmlArtifact.fullscreen': 'Full screen',
  'htmlArtifact.exitFullscreen': 'Exit full screen',
  'htmlArtifact.truncated':
    'Showing the first {count, number} characters. Download the file for the full source.',

  // ── Chat (conversation view) ───────────────────────────────────────
  'chat.input.placeholder': 'Assign a task or ask anything...',
  'chat.input.characterCount': '{count} / {max} characters',
  'chat.thinking': 'Thinking...',
  'chat.disclaimer.combined': '⚠️ Aibuild OS (Beta): Agents can make mistakes, and commands execute unverified with full system access. Always review outputs.{br}Feedback: support@ai-build.com.',
  'chat.runner.cloud': 'Cloud Runner',
  'chat.runner.noLocal': 'No local runners',
  'chat.runner.status.online': 'Online',
  'chat.runner.status.busy': 'Busy',
  'chat.runner.status.offline': 'Offline',

  // ── Plan mode ─────────────────────────────────────────────────────

  // ── Model-swap suggestion (AIB-7877) ──────────────────────────────
  'chat.modelSuggestion.title': 'Try a better-suited model',
  'chat.modelSuggestion.body': '{model} is better suited for this kind of task. Switch to it and continue?',
  'chat.modelSuggestion.dismiss': 'Continue with current model',
  'chat.modelSuggestion.accept': 'Switch to {model} and continue',

  // ── Guardrail ─────────────────────────────────────────────────────
  'chat.guardrail.retracted': 'This response was flagged and removed by our content safety system. If you believe this was a mistake, try rephrasing your request or contact support.',

  // ── Image unavailable ─────────────────────────────────────────────
  'chat.image.unavailable': "This image couldn't be generated due to our content guidelines.",

  'chat.artifact.downloadFailed': "Couldn't download {name}. Check your connection and try again.",
  'chat.artifact.downloadFailedHttp': "Couldn't download {name}.",
  'chat.artifact.downloadRetry': 'Retry',

  // ── Environment status ────────────────────────────────────────────
  'chat.environment.allocating': 'Starting execution environment... ({elapsed})',
  'chat.environment.allocating.hint': 'Booting a Windows machine \u2014 this can take a few minutes',
  'chat.environment.ready': 'Environment ready',
  'chat.trail.working': 'Working for {elapsed}',
  'chat.trail.workingUntimed': 'Working',
  'chat.trail.worked': 'Worked for {elapsed}',
  'chat.trail.workedUntimed': 'Worked',
  'chat.trail.steps': '{count, plural, one {# step} other {# steps}}',
  'chat.trail.failed': '{count} failed',
  'chat.trail.awaitingApproval': 'Waiting for your approval',
  'chat.trail.environment': 'Starting execution environment...',

  // ── Home page ────────────────────────────────────────────────────────
  'home.title': 'Home',
  'home.help': 'Your starting point — assign a task or ask anything, attach files, or kick off a process, and the AI agent takes it from there.',

  // ── Task prompt ──────────────────────────────────────────────────────
  'taskPrompt.heroTitle': 'What can I do for you?',
  'taskPrompt.heroTitle.withName': 'What can I do for you, {firstName}?',
  'taskPrompt.uploadButton.title': 'Upload files or images',
  'taskPrompt.inputPlaceholder': 'Assign a task or ask anything...',
  'taskPrompt.sendButton.title': 'Send message',
  'taskPrompt.processes.label': 'Processes',
  'taskPrompt.processes.lfamMould': 'LFAM mould',
  'taskPrompt.processes.thermalAnalysis': 'Thermal analysis',
  'taskPrompt.processes.more': 'More',
  'taskPrompt.examples.topologyBracket': 'Generate an image of a topology optimised bracket',
  'taskPrompt.examples.tellMeWhatYouCanDo': 'Tell me what you can do',
  'taskPrompt.examples.droneMesh': 'Make a 3D mesh of a drone',
  'taskPrompt.examples.processEditor': 'What is the process editor?',
  'taskPrompt.examples.engineeringDrawing.chip': 'Turn engineering drawing into CAD',
  'taskPrompt.examples.engineeringDrawing.prompt': 'I am attaching a scanned 2D engineering drawing (image) of a legacy aerospace part — multi-view with dimensioned GD&T. Interpret the views, infer the projection standard (state assumption if unmarked), reconstruct a parametric STEP model that honours every called-out dimension and tolerance, and flag any dimension that was ambiguous. Persona: Aerospace Design Engineer at OEM (PLM context). Verification: bounding box vs drawing, hole diameter check, mass comparison. Success: every drawing dimension preserved within 0.1 mm. Optional next step: ask if I\'d like to use CAM or 3D printing or FEA and explain which tools are available for either.',

  // ── Navigation ───────────────────────────────────────────────────────
  'nav.home': 'Home',
  'nav.processes': 'Processes',
  'nav.artifacts': 'Artifacts',
  'nav.instructions': 'Instructions',
  'nav.skills': 'Skills',
  'nav.settings': 'Settings',
  'nav.helpSupport': 'Help & Support',
  'settings.account.logout': 'Logout',
  'nav.sidebar.openMenu': 'Open menu',
  'nav.sidebar.closeMenu': 'Close menu',
  'nav.sidebar.collapse': 'Collapse sidebar',
  'nav.sidebar.expand': 'Expand sidebar',
  'nav.sidebar.switchWorkspace': 'Switch workspace',

  // ── Product switcher ─────────────────────────────────────────────────
  'productSwitcher.ariaLabel': 'Product switcher',
  'productSwitcher.aibuildOs': 'Aibuild OS',
  'productSwitcher.aibuildCam': 'Aibuild CAM',
  'productSwitcher.aibuildCnc': 'Aibuild CNC',
  'productSwitcher.aibuildFets': 'Aibuild FETS',

  // ── Conversation list ────────────────────────────────────────────────
  'conversations.title': 'Tasks',
  'conversations.loading': 'Loading...',
  'conversations.loadingMore': 'Loading more...',
  'conversations.empty': 'No conversations yet',
  'conversations.newConversation': 'New conversation',
  'conversations.moreOptions': 'More options',
  'conversations.running.label': 'Working',
  'conversations.running.count': '{count, plural, one {# task working} other {# tasks working}}',
  'conversations.rename.label': 'Rename',
  'conversations.delete.label': 'Delete',

  // ── Conversation delete dialog ───────────────────────────────────────
  'conversation.rename.title': 'Rename conversation',
  'conversation.rename.placeholder': 'Enter a new name',
  'conversation.rename.confirm': 'Rename',
  'conversation.rename.cancel': 'Cancel',
  'conversation.delete.untitled': 'New conversation',
  'conversation.delete.title': 'Delete conversation?',
  'conversation.delete.subtitle': 'This will permanently delete "{title}".',
  'conversation.delete.confirm': 'Delete',
  'conversation.delete.cancel': 'Cancel',

  // ── Processes page ──────────────────────────────────────────────────
  'processes.title': 'Processes',
  'processes.placeholder': 'Process management coming soon.',
  'processes.assistant.title': 'Process assistant',
  'processes.assistant.alpha': 'Alpha',
  'processes.assistant.open': 'Show the process assistant',
  'processes.assistant.close': 'Hide the process assistant',
  'processes.assistant.working': 'The process assistant is working…',
  'processes.assistant.placeholder': 'Describe your process…',
  'processes.assistant.unavailable':
    "The process assistant isn't available for this process.",

  // ── Artifacts page ─────────────────────────────────────────────────
  'artifacts.title': 'Artifacts',
  'artifacts.placeholder': 'Artifact management coming soon.',
  'artifacts.loading': 'Loading artifacts...',
  'artifacts.empty': 'No artifacts yet. Artifacts are files produced during AI execution — screenshots, generated models, G-code, and more.',

  // ── Skills page ────────────────────────────────────────────────────
  'skills.title': 'Skills',
  'skills.help': 'Instruction bundles the agent loads when a task needs them. Use the built-in ones, write your own, or share one with your organization.',
  'skills.description':
    'Skills are instruction bundles the AI agent can load on demand. Built-in skills ship with the platform and are read-only. Your own skills teach your agent your workflows without affecting anyone else, and you can share one with the whole organization — it reaches every colleague switched off, for each of them to turn on if they want it.',
  'skills.loading': 'Loading skills...',
  'skills.loadError': 'Failed to load skills. Please try again later.',
  'skills.personal.title': 'Your skills',
  'skills.personal.empty':
    'No skills of your own yet. Create one to give your agent reusable instructions — only you will see it.',
  'skills.custom.title': 'Organization skills',
  'skills.custom.new': 'New Skill',
  'skills.custom.edit': 'Edit',
  'skills.custom.delete': 'Delete',
  'skills.custom.empty':
    "No organization skills yet. Create one and share it to give every member's agent the same instructions, switched off until each of them turns it on.",
  'skills.custom.optIn':
    'Skills shared with your organization start switched off. Turn one on when you want your agent to use it — nobody else is affected either way.',
  'skills.toggle.label': 'Use the {name} skill',
  'skills.builtIn.title': 'Built-in skills',
  'skills.builtIn.badge': 'Built-in',
  'skills.builtIn.tools': 'Tools: {tools}',
  'skills.builtIn.view': 'View',
  'skills.builtIn.copy': 'Copy instructions',
  'skills.builtIn.copied': 'Copied',
  'skills.builtIn.copyFailed':
    'Could not copy to the clipboard. Select the text and copy it manually.',
  'skills.builtIn.close': 'Close',
  'skills.builtIn.readOnlyHint':
    'Read-only. Copy this into a new organization skill to experiment with changes.',
  'skills.dialog.editTitle': 'Edit skill',
  'skills.dialog.createTitle': 'New skill',
  'skills.dialog.save': 'Save',
  'skills.dialog.cancel': 'Cancel',
  'skills.form.name': 'Name',
  'skills.form.namePlaceholder': 'e.g. gdt_annotations',
  'skills.form.nameHint': '2-64 characters: lowercase letter first, then lowercase letters, digits, _ or -',
  'skills.form.description': 'Description',
  'skills.form.descriptionPlaceholder': 'One-line summary the agent uses to decide when to load this skill',
  'skills.form.instructions': 'Instructions',
  'skills.form.instructionsPlaceholder': 'Guidance the agent receives when it loads this skill...',
  'skills.form.tools': 'Tools',
  'skills.form.toolsHint': 'Extra tools the agent gains when it loads this skill. General-purpose tools such as run_code are always available. Badged tools only work on one runner type and are ignored elsewhere.',
  'skills.form.localOnly': 'Local only',
  'skills.form.cloudOnly': 'Cloud only',
  'skills.form.visibility': 'Visibility',
  'skills.form.share': 'Share with the organization',
  'skills.form.shareHint':
    "Shared skills appear on every member's Skills page switched off, so nobody's agent changes until they turn one on. Leave unchecked to keep the skill to yourself.",
  'skills.form.visibilityWillShare':
    'Saving will offer this skill and its instructions to every member, switched off until each of them turns it on.',
  'skills.form.visibilityWillUnshare':
    "Saving will remove this skill from every other member's agent, and any process step that uses it will need updating. Only you will be able to load it.",
  'skills.delete.title': 'Delete Skill',
  'skills.delete.confirm': 'Delete',
  'skills.delete.message': 'Delete skill "{name}"? The agent will no longer be able to load it.',

  // ── Instructions page ────────────────────────────────────────────
  'instructions.title': 'Instructions',
  'instructions.help': 'Standing instructions the AI agent reads at the start of every task — your own, and your team\'s.',
  'instructions.tab.personal': 'Personal',
  'instructions.tab.team': 'Team',
  'instructions.description.personal': 'Add personal instructions for the AI agent. These apply only to your conversations and take precedence over team instructions when they conflict.',
  'instructions.description.team': 'Add instructions that will be available to the AI agent at the start of every new conversation. These instructions are shared across your organization.',
  'instructions.description.teamReadOnly': 'These are your organization\'s shared instructions for the AI agent. Only admins can edit team instructions.',
  'instructions.textareaPlaceholder.personal': 'Enter your personal instructions for the AI agent...\n\nFor example:\n• "Always respond in a formal tone"\n• "I work with Blender and prefer metric units"\n• "When generating G-code, use a layer height of 2mm"',
  'instructions.textareaPlaceholder.team': 'Enter instructions for the AI agent...\n\nFor example:\n• "Always respond in a formal tone"\n• "Our company manufactures aerospace parts using LFAM processes"\n• "When generating G-code, use metric units and a layer height of 2mm"',
  'instructions.characterCount': '{count} / {max} characters',
  'instructions.save': 'Save',
  'instructions.saved.personal': 'Personal instructions saved successfully',
  'instructions.saved.team': 'Team instructions saved successfully',
  'instructions.saveError': 'Failed to save instructions',
  'instructions.loading': 'Loading instructions...',
  'instructions.loadError': 'Failed to load instructions. Please try again later.',

  // ── Verify email page ──────────────────────────────────────────────
  'verifyEmail.title': 'Enter verification code',
  'verifyEmail.subtitle': 'We sent a 6-digit code to {email}. Enter it below to verify your account.',
  'verifyEmail.verified.title': 'Email verified!',
  'verifyEmail.verified.subtitle': 'Your email has been verified. Redirecting to login...',
  'verifyEmail.verifying.title': 'Verifying...',
  'verifyEmail.verifying.subtitle': 'Confirming your email address...',
  'verifyEmail.codeResent': "We've re-sent a new verification code to your email.",
  'verifyEmail.error.fallback': 'Verification failed. Please try again.',
  'verifyEmail.error.verificationFailed': 'Verification failed',
  'verifyEmail.error.resendFailed': 'Failed to resend code. Please try again.',
  'verifyEmail.button.verify': 'Verify Email',
  'verifyEmail.button.verifying': 'Verifying...',
  'verifyEmail.button.backToLogin': 'Back to login',
  'verifyEmail.resend.prompt': "Didn't receive the code?",
  'verifyEmail.resend.countdown': 'Resend in {seconds}s',
  'verifyEmail.resend.action': 'Resend code',

  // ── Settings page ──────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.help': 'Your account, preferences, security, billing, organization and local runners.',
  'settings.placeholder': 'Settings coming soon.',
  'settings.account': 'Account',
  'settings.security': 'Security',

  // ── Account settings ──────────────────────────────────────────────
  'settings.account.title': 'Account',
  'settings.account.profile': 'Profile',
  'settings.account.name': 'Name',
  'settings.account.email': 'Email',
  'settings.account.loading': 'Loading...',
  'settings.account.name.updated': 'Name updated successfully',
  'settings.account.saving': 'Saving...',
  'settings.account.save': 'Save',
  'settings.account.cancel': 'Cancel',
  'settings.account.appearance': 'Appearance',
  'settings.account.theme': 'Theme',
  'settings.account.theme.SYSTEM': 'System',
  'settings.account.theme.light': 'Light',
  'settings.account.theme.dark': 'Dark',

  // ── Security settings ─────────────────────────────────────────────
  'settings.security.title': 'Two-Factor Authentication',
  'settings.security.loading': 'Loading security settings...',
  'settings.security.loadError': 'Failed to load MFA status',
  'settings.security.mfaLabel': 'Two-factor authentication',
  'settings.security.mfaDescription': 'Add an extra layer of security by requiring a verification code from your authenticator app when signing in.',
  'settings.security.mfaEnabled': 'Two-factor authentication enabled',
  'settings.security.mfaDisabled': 'Two-factor authentication disabled',
  'settings.security.orgEnforced': 'Your organization requires 2FA. Contact your admin to change this policy.',
  'settings.security.disableTitle': 'Disable Two-Factor Authentication',
  'settings.security.disableConfirm': 'Disable 2FA',
  'settings.security.disableWarning': 'Are you sure you want to disable two-factor authentication? This will make your account less secure.',
  'settings.security.generating': 'Generating your authenticator secret...',
  'settings.security.step1': 'Scan QR Code',
  'settings.security.step1Description': 'Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and scan the QR code below.',
  'settings.security.manualEntry': 'Or enter this secret key manually:',
  'settings.security.copied': 'Copied!',
  'settings.security.copy': 'Copy',
  'settings.security.step2': 'Enter Verification Code',
  'settings.security.step2Description': 'Enter the 6-digit code shown in your authenticator app.',
  'settings.security.verifying': 'Verifying...',
  'settings.security.verifyEnable': 'Verify & Enable',
  'settings.security.cancel': 'Cancel',

  // ── Organization settings ─────────────────────────────────────────
  'org.title': 'Organization',
  'org.loading': 'Loading...',
  'org.loggedInAs': 'Logged in as',
  'org.info.title': 'Organization',
  'org.name.label': 'Name',
  'org.name.placeholder': 'Organization name',
  'org.name.updated': 'Organization name updated',
  'org.type': 'Type',
  'org.type.personal': 'Personal',
  'org.type.team': 'Team',
  'org.edit': 'Edit',
  'org.save': 'Save',
  'org.cancel': 'Cancel',
  'org.security.title': 'Security',
  'org.mfa.title': 'MFA Enforcement',
  'org.mfa.label': 'Require two-factor authentication',
  'org.mfa.enabledDescription': 'All members must enable 2FA before accessing the platform.',
  'org.mfa.disabledDescription': '2FA is optional for members.',
  'org.mfa.adminOnly': 'Only organization admins can change this setting.',
  'org.mfa.message': 'Are you sure you want to {action} MFA enforcement for all organization members?',
  'org.mfa.enable': 'Enable',
  'org.mfa.disable': 'Disable',
  'org.mfa.success': 'MFA enforcement {action}d',
  'org.members.title': 'Members ({count})',
  'org.members.name': 'Name',
  'org.members.email': 'Email',
  'org.members.role': 'Role',
  'org.members.expiry': 'Expiry',
  'org.members.actions': 'Actions',
  'org.role.updated': 'Role updated',
  'org.remove.title': 'Remove Member',
  'org.remove.message': 'Remove {email} from the organization?',
  'org.remove.confirm': 'Remove',
  'org.remove.button': 'Remove',
  'org.remove.success': '{email} removed',
  'org.invite.title': 'Invite Members',
  'org.invite.emailLabel': 'Email address',
  'org.invite.roleLabel': 'Role',
  'org.invite.sending': 'Sending...',
  'org.invite.send': 'Send Invite',
  'org.invite.success': 'Invitation sent to {email}',
  'org.invite.revoked': 'Invitation to {email} revoked',
  'org.memberLimit.title': 'Team size limit reached',
  'org.memberLimit.message': 'Your team has reached the {limit}-member limit. To add more seats, contact us at support@ai-build.com.',
  'org.memberLimit.cta': 'OK',
  'org.invitations.title': 'Pending Invitations',
  'org.invitations.sent': 'Sent',
  'org.invitations.expires': 'Expires',
  'org.invitations.revoke': 'Revoke',
  'org.expiry.selectDate': 'Please select an expiry date',
  'org.expiry.updated': 'Expiry date updated',
  'org.leave.title': 'Leave Organization',
  'org.leave.message': 'You will lose access to all organization assets and revert to your personal plan. This action cannot be undone.',
  'org.leave.confirm': 'Leave Organization',
  'org.leave.button': 'Leave Organization',
  'org.leave.success': 'Left organization. Returning to personal organization.',
  'org.leave.pendingInvites': 'Cannot leave while there are pending invitations. Revoke them first.',
  'org.switch.title': 'Switch Organization',
  'org.create.description': 'Create a new organization to collaborate with a different team. You will be moved to the new organization as its admin.',
  'org.create.nameLabel': 'New organization name',
  'org.create.placeholder': 'e.g. Acme Corp',
  'org.create.button': 'Create',
  'org.create.creating': 'Creating...',
  'org.create.team': 'Create Team Organization',
  'org.create.success': 'Organization created! Reloading...',

  // ── Local runners ─────────────────────────────────────────────────
  'runners.description': 'Run Aibuild OS agents on your own machine using your local CAD software.',
  'runners.error.loadKeys': 'Failed to load API keys',
  'runners.error.generateKey': 'Failed to generate API key',
  'runners.error.revokeKey': 'Failed to revoke API key',
  'runners.error.deleteRunner': 'Failed to delete runner',
  'runners.key.generated': 'API key generated',
  'runners.key.revoked': 'API key revoked',
  'runners.copied': 'Copied to clipboard',
  'runners.runner.deleted': 'Runner "{name}" deleted',
  'runners.delete.title': 'Delete Runner',
  'runners.delete.confirm': 'Delete',
  'runners.delete.message': "Delete runner \"{name}\"? It will be removed from the list. If it's still running, it will re-register on next heartbeat.",
  'runners.apiKeys.title': 'API Keys',
  'runners.apiKeys.nameLabel': 'Key Name',
  'runners.apiKeys.namePlaceholder': "Name your key, e.g. My Workstation",
  'runners.apiKeys.nameDuplicate': 'A key with this name already exists',
  'runners.apiKeys.generating': 'Generating...',
  'runners.apiKeys.generate': 'Generate Key',
  'runners.apiKeys.warning': 'Copy this key now — it will not be shown again.',
  'runners.apiKeys.name': 'Name',
  'runners.apiKeys.key': 'Key',
  'runners.apiKeys.created': 'Created',
  'runners.apiKeys.lastUsed': 'Last Used',
  'runners.apiKeys.revoke': 'Revoke',
  'runners.apiKeys.empty': 'No API keys yet. Generate one to connect a local runner.',
  'runners.copy': 'Copy',
  'runners.connected.title': 'Connected Runners',
  'runners.connected.status': 'Status',
  'runners.connected.name': 'Name',
  'runners.connected.apps': 'Apps',
  'runners.connected.lastSeen': 'Last Seen',
  'runners.connected.delete': 'Delete',
  'runners.connected.empty': 'No runners connected. Start a runner on your machine to see it here.',
  'runners.download.label': 'Download the runner to connect your local CAD software to Aibuild OS.',
  'runners.download.button': 'Download Runner',
  'runners.download.downloading': 'Preparing download...',
  'runners.download.error': 'Failed to start download',
  'runners.info.title': 'Aibuild Local Runner',
  'runners.info.close': 'Close',
  'runners.info.ariaLabel': 'About Local Runner',

  // ── Marketplace ──────────────────────────────────────────────────────
  'marketplace.title': 'Marketplace',
  'marketplace.action.download': 'Download',
  'marketplace.action.contactSales': 'Contact Sales',
  'marketplace.action.purchase': 'Purchase',
  'marketplace.action.included': 'Included',
  'marketplace.runner.cloud': 'Cloud',
  'marketplace.runner.local': 'Local',
  'marketplace.contactSales.title': 'Contact Sales',
  'marketplace.contactSales.body': 'To enable this module for your organization, please contact our sales team. We will help you find the right plan for your needs.',
  'marketplace.contactSales.emailPrompt': 'Email us at {email}',
  'marketplace.contactSales.close': 'Close',
  'marketplace.skillInfo.close': 'Close',

  // ── Billing settings ────────────────────────────────────────────────
  'billing.title': 'Billing',
  'billing.loading': 'Loading billing information...',
  'billing.loadFailed': 'Unable to load billing information.',
  'billing.error.loadOverview': 'Failed to load billing information',
  'billing.error.loadTransactions': 'Failed to load transaction history',
  'billing.error.purchaseFailed': 'Failed to start purchase',
  'billing.error.portalFailed': 'Failed to open billing portal',
  'billing.cancellation.banner':
    'Your subscription is scheduled to cancel on <b>{date}</b>. You will retain access until then.',
  'billing.plan.title': 'Current Plan',
  'billing.plan.status': 'Status',
  'billing.plan.label': 'Plan',
  'billing.plan.none': 'No plan',
  'billing.plan.free': 'Free',
  'billing.plan.migrated_free': 'Free (Legacy)',
  'billing.plan.benchmark': 'Benchmark',
  'billing.plan.starter': 'Starter',
  'billing.plan.business': 'Business',
  'billing.plan.billingPeriod': 'Billing Period',
  'billing.plan.currentPeriod': 'Current Period',
  'billing.plan.manageSubscription': 'Manage Subscription',
  'billing.plan.upgrade': 'Upgrade Plan',
  'billing.upgrade.title': 'Upgrade your plan',
  'billing.upgrade.subtitle': 'Choose a plan to unlock full capabilities and manage your subscription.',
  'billing.status.cancelling': 'Cancelling',
  'billing.balance.title': 'Credit Balance',
  'billing.balance.unit': 'credits available',
  'billing.balance.breakdown': 'Credit breakdown by batch:',
  'billing.batch.info': '{remaining} / {original} credits — expires {expiresAt}',
  'billing.batch.created': 'Added {date}',
  'billing.sourceType.subscription': 'Subscription',
  'billing.sourceType.purchase': 'Purchased',
  'billing.sourceType.adminGrant': 'Admin Grant',
  'billing.sourceType.migratedGrant': 'Migrated',
  'billing.purchase.title': 'Buy Additional Credits',
  'billing.purchase.description':
    'On-demand credits at ${price}/credit. Credits are valid for 12 months from purchase.',
  'billing.purchase.batchLimitReached':
    'You already have {count} active credit batches. Please use your existing credits before purchasing more.',
  'billing.purchase.quantity': 'Quantity',
  'billing.purchase.quantityRange': 'Min: {min} credits — Max: {max} credits',
  'billing.purchase.redirecting': 'Redirecting...',
  'billing.purchase.button': 'Purchase Credits',
  'billing.transactions.title': 'Transaction History',
  'billing.transactions.loading': 'Loading...',
  'billing.transactions.empty': 'No transactions yet.',
  'billing.transactions.date': 'Date',
  'billing.transactions.type': 'Type',
  'billing.transactions.amount': 'Amount',
  'billing.transactions.balanceAfter': 'Balance After',
  'billing.transactions.source': 'Source',
  'billing.transactions.previous': 'Previous',
  'billing.transactions.pageInfo': 'Page {current} of {total}',
  'billing.transactions.next': 'Next',
  'billing.txType.usage': 'Usage',
  'billing.txType.consumption': 'Consumption',
  'billing.txType.subscription': 'Subscription Credit',
  'billing.txType.purchase': 'Purchase',
  'billing.txType.bonus': 'Bonus',
  'billing.txType.refund': 'Refund',
  'billing.txType.adminGrant': 'Admin Grant',
  'billing.txType.adminDebit': 'Admin Debit',
  'billing.txType.migratedGrant': 'Migration Credit',
  'billing.txType.expiry': 'Expired',
  'billing.usage.loading': 'Loading...',
  'billing.usage.empty': 'No transactions yet.',
  'billing.usage.credits': 'credits',
  'billing.usage.inputTokens': '{count} input',
  'billing.usage.outputTokens': '{count} output',
  'billing.usage.cacheReadTokens': '{count} cached',
  'billing.usage.cacheWriteTokens': '{count} cache write',
  'billing.usage.tokensLabel': 'tokens',
  'billing.usage.iterations': '{count, plural, one {# iteration} other {# iterations}}',
  'billing.usage.messages': '{count, plural, one {# message} other {# messages}}',
  'billing.usage.nodes': '{count, plural, one {# node} other {# nodes}}',
  'billing.usage.loadingDetail': 'Loading details...',
  'billing.usage.loadError': 'Failed to load transactions.',
  'billing.usage.loadFailed': 'Failed to load details.',
  'billing.usage.retry': 'Retry',
  'billing.usage.legacyUsage': 'Pre-tracking usage',
  'billing.usage.legacyTransactions': '{count, plural, one {# transaction} other {# transactions}}',
  'billing.usage.legacyShowDetails': 'Show details',
  'billing.usage.legacyHideDetails': 'Hide details',
  'billing.sourceApp.cam': 'Aibuild CAM',
  'billing.sourceApp.os': 'Aibuild OS',
  'billing.sourceApp.osProcess': 'Aibuild OS — Processes',
  'billing.processRun.untitled': 'Untitled process',
  'billing.processRun.runNumber': 'Run #{number}',
  'billing.processRun.testRun': 'Test run',

  'billing.refunds.title': 'Refunds',
  'billing.refunds.description': 'For refund requests, please contact our support team at',

  // ── Plan picker ───────────────────────────────────────────────────
  'planPicker.title': 'Choose the right plan for your team',
  'planPicker.subtitle': 'Pay only for what you use. Scale with credits as your engineering projects grow.',
  'planPicker.loading': 'Loading plans...',
  'planPicker.error.loadFailed': 'Failed to load plans',
  'planPicker.error.checkoutFailed': 'Failed to start checkout',
  'planPicker.monthly': 'Monthly',
  'planPicker.yearly': 'Yearly',
  'planPicker.save': 'Save 16%',
  'planPicker.billedYearly': 'Billed yearly',
  'planPicker.billedMonthly': 'Billed monthly',
  'planPicker.redirecting': 'Redirecting...',
  'planPicker.getStarted': 'Get Started',
  'planPicker.creditsPerMonth': 'Credits per month',
  'planPicker.mostPopular': 'Most Popular',
  'planPicker.everythingInStarter': 'Everything in Starter, plus',
  'planPicker.enterpriseSubtitle': 'For large teams & orgs',
  'planPicker.contactSales': 'Contact Sales',
  'planPicker.everythingInBusiness': 'Everything in Business, plus',

  // ── Tabbed panel ──────────────────────────────────────────────────
  'tabbedPanel.tab.marketplace': 'Marketplace',
  'tabbedPanel.tab.processes': 'Processes',
  'tabbedPanel.processes.placeholder': 'Processes coming soon',

  // ── Tool display names ───────────────────────────────────────────────
  // Skill loading (single-agent mode)
  'tool.load_skill': 'Load skill',
  'tool.load_skill.loading': 'Loading {skill} skill...',
  'tool.load_skill.loadingGeneric': 'Loading skill...',
  'tool.load_skill.loaded': '{skill} skill loaded',
  'tool.load_skill.loadedGeneric': 'Skill loaded',
  // Runner tools
  'tool.run_code': 'Run code',
  'tool.start_process': 'Start process',
  'tool.list_windows': 'List windows',
  'tool.sleep': 'Wait',
  'tool.present_artifact': 'Present artifact',
  'tool.invoke_agent': 'Invoke agent',
  'tool.request_tool_execution': 'Request execution',
  // CAM agent — knowledge base
  'tool.search_knowledge_base': 'Search knowledge base',
  // CAM agent — materials
  'tool.listMaterials': 'List materials',
  'tool.getMaterialDetails': 'Get material details',
  // CAM agent — printers
  'tool.listPrinters': 'List printers',
  'tool.getPrinterDetails': 'Get printer details',
  // CAM agent — projects
  'tool.listProjects': 'List projects',
  'tool.createProject': 'Create project',
  'tool.getProjectDetails': 'Get project details',
  'tool.deleteProject': 'Delete project',
  // CAM agent — files
  'tool.listFiles': 'List files',
  'tool.getFileDownloadUrl': 'Get file download URL',
  'tool.deleteFile': 'Delete file',
  'tool.importFileFromUrl': 'Import file from URL',
  'tool.exportFileToWorkspace': 'Export file to workspace',
  // CAM agent — workflows
  'tool.listWorkflows': 'List workflows',
  'tool.listRecentWorkflows': 'List recent workflows',
  'tool.createWorkflow': 'Create workflow',
  'tool.getWorkflowDetails': 'Get workflow details',
  'tool.deleteWorkflow': 'Delete workflow',
  'tool.computeWorkflow': 'Compute workflow',
  'tool.getComputationStatus': 'Get computation status',
  // CAM agent — operators
  'tool.listAvailableOperators': 'List available operators',
  'tool.getOperatorDefinition': 'Get operator definition',
  'tool.addOperatorToWorkflow': 'Add operator to workflow',
  'tool.deleteOperatorFromWorkflow': 'Delete operator from workflow',
  'tool.getOperatorDetails': 'Get operator details',
  'tool.updateOperatorValues': 'Update operator values',
  // CAM agent — results
  'tool.getQualityCheckResults': 'Get quality check results',
  'tool.getToolpathMetrics': 'Get toolpath metrics',
  'tool.getToolpathInstructions': 'Get toolpath instructions',
  // CAM agent — postprocessor
  'tool.testExternalPostprocessor': 'Test postprocessor',

  // ── Agent names (for plan cards) ─────────────────────────────────────
  'agent.os': 'OS',
  'agent.cam': 'Aibuild CAM',
  'agent.freecad': 'FreeCAD',
  'agent.blender': 'Blender',
  'agent.rhino': 'Rhino',
  'agent.cadquery': 'CadQuery',
  'agent.pymeshlab': 'PyMeshLab',
  'agent.prusaslicer': 'PrusaSlicer',
  'agent.engineeringdrawinginterpreter': 'Engineering Drawing Interpreter',

  // ── Tool display names ──────────────────────────────────────────────
  'tool.reflect_memory': 'Checking memory',
  'tool.recall_memories': 'Searching memory',
  'tool.remember': 'Saving to memory',


  // ── Model Viewer ──────────────────────────────────────────────────────
  'modelViewer.aiRender': 'AI render of this view',
  'modelViewer.aiRender.action': 'Render · {credits} credits',
  'modelViewer.aiRender.back': 'Back to 3D',
  'modelViewer.aiRender.captureFailed': 'This view could not be captured. Try again once the model is on screen.',
  'modelViewer.aiRender.download': 'Download',
  'modelViewer.aiRender.failed': 'The render could not be generated. Try again.',
  'modelViewer.aiRender.model': 'Model',
  'modelViewer.aiRender.offerFailed': 'AI renders are unavailable right now.',
  'modelViewer.aiRender.pending': 'Render · – credits',
  'modelViewer.aiRender.rendering': 'Rendering…',
  'modelViewer.aiRender.result': 'AI render',
  'modelViewer.aiRender.theme': 'Theme',
  'modelViewer.aiRender.theme.clay': 'Clay model',
  'modelViewer.aiRender.theme.metal': 'Machined metal',
  'modelViewer.aiRender.theme.outdoor': 'Outdoor',
  'modelViewer.aiRender.theme.studio': 'Studio',
  'modelViewer.background.load': 'Load a background image',
  'modelViewer.background.remove': 'Remove background image',
  'modelViewer.background.unreadable': 'That image could not be opened',
  'modelViewer.background.unsupported': 'Use a PNG, JPG or WebP image under 20 MB',
  'modelViewer.close': 'Close 3D viewer',
  'modelViewer.dimensions': 'Dimensions',
  'modelViewer.empty': 'No 3D model yet',
  'modelViewer.environment': 'Scene',
  'modelViewer.environment.centre': 'Centre on origin',
  'modelViewer.environment.grid': 'Grid',
  'modelViewer.environment.occlusion': 'Ambient occlusion',
  'modelViewer.environment.platform': 'Build platform',
  'modelViewer.environment.shadows': 'Shadows',
  'modelViewer.environment.sit': 'Sit on grid',
  'modelViewer.environment.underLight': 'Under-light',
  'modelViewer.expand': 'Open full screen',
  'modelViewer.fitView': 'Fit view',
  'modelViewer.grid': 'Grid {spacing}',
  'modelViewer.grid.follow': 'Follow the zoom',
  'modelViewer.grid.menu': 'Grid spacing',
  'modelViewer.hint.mouse': 'Drag to rotate · Scroll to zoom · Right-click to pan',
  'modelViewer.hint.touch': 'Drag to rotate · Pinch to zoom · Two-finger pan',
  'modelViewer.keys.escape': 'Close, stop the tool or clear the selection',
  'modelViewer.keys.fit': 'Fit the whole part',
  'modelViewer.keys.measure': 'Measure distance',
  'modelViewer.keys.projection': 'Orthographic or perspective',
  'modelViewer.keys.sectionSize': 'Grow or shrink the section plane',
  'modelViewer.keys.shading': 'Edged faces, shaded, ghosted, wireframe',
  'modelViewer.keys.zoom': 'Zoom in where the cursor points',
  'modelViewer.loadModelFirst': 'Load a model first',
  'modelViewer.loading': 'Loading 3D model…',
  'modelViewer.measure': 'Measure distance',
  'modelViewer.measure.delta': 'Δ{axis}',
  'modelViewer.measure.distance': 'Distance',
  'modelViewer.measure.hintFirst': 'Click a point on the part',
  'modelViewer.measure.hintSecond': 'Click a second point',
  'modelViewer.measure.tooDense': 'Measure is unavailable on a model this detailed',
  'modelViewer.model': 'Model',
  'modelViewer.model.tooDense': 'Too detailed to select parts or measure',
  'modelViewer.navigation': 'Navigation',
  'modelViewer.navigation.close': 'Close navigation help',
  'modelViewer.navigation.keyboard': 'Keyboard',
  'modelViewer.navigation.mouse': 'Navigation',
  'modelViewer.navigation.orbit': 'Orbit',
  'modelViewer.navigation.orbit.hint': '(drag)',
  'modelViewer.navigation.orbit.touchHint': '(one finger)',
  'modelViewer.navigation.pan': 'Pan',
  'modelViewer.navigation.pan.hint': '(right-drag)',
  'modelViewer.navigation.pan.touchHint': '(two fingers)',
  'modelViewer.navigation.select': 'Select',
  'modelViewer.navigation.select.hint': '(click, Shift to add, double-click for the body)',
  'modelViewer.navigation.select.touchHint': '(tap)',
  'modelViewer.navigation.zoom': 'Zoom',
  'modelViewer.navigation.zoom.hint': '(scroll)',
  'modelViewer.navigation.zoom.touchHint': '(pinch)',
  'modelViewer.panel.close': 'Close',
  'modelViewer.part': 'Part {number}',
  'modelViewer.part.size': 'Size',
  'modelViewer.part.surfaceArea': 'Surface area',
  'modelViewer.part.volume': 'Volume',
  'modelViewer.retry': 'Retry',
  'modelViewer.section': 'Section',
  'modelViewer.section.exit': 'Exit section',
  'modelViewer.section.flip': 'Flip section',
  'modelViewer.section.offset': 'Offset',
  'modelViewer.section.resetRotation': 'Reset rotation',
  'modelViewer.section.rotate': 'Rotate about {axis}',
  'modelViewer.select.bodies': 'Select bodies',
  'modelViewer.select.vertices': 'Select vertices',
  'modelViewer.shading': 'Shading',
  'modelViewer.shading.edged': 'Edged faces',
  'modelViewer.shading.ghosted': 'Ghosted',
  'modelViewer.shading.shaded': 'Shaded',
  'modelViewer.shading.wireframe': 'Wireframe',
  'modelViewer.tools': 'Viewport tools',
  'modelViewer.topBar': 'View tools',
  'modelViewer.unavailable': '3D view unavailable',
  'modelViewer.underLight.around': 'Around',
  'modelViewer.underLight.colour': 'Colour',
  'modelViewer.underLight.intensity': 'Intensity',
  'modelViewer.underLight.settings': 'Under-light settings',
  'modelViewer.underLight.tilt': 'Tilt',
  'modelViewer.view.back': 'Back',
  'modelViewer.view.bottom': 'Bottom',
  'modelViewer.view.front': 'Front',
  'modelViewer.view.isometric': 'Isometric view',
  'modelViewer.view.left': 'Left',
  'modelViewer.view.options': 'View options',
  'modelViewer.view.perspective': 'Perspective view',
  'modelViewer.view.reset': 'Reset view',
  'modelViewer.view.right': 'Right',
  'modelViewer.view.top': 'Top',

  // ── Onboarding (post-payment lead-qualification form) ─────────────────
  'onboarding.stepCounter': '{current} / {total}',
  'onboarding.continue': 'Continue',
  'onboarding.back': 'Back',
  'onboarding.finish': 'Finish',
  'onboarding.skip': 'Skip',
  'onboarding.skip.confirm.title': 'Skip customization?',
  'onboarding.skip.confirm.body':
    'If you skip this, your workspace will be set up with default settings. You can customize it anytime later.',
  'onboarding.skip.confirm.confirm': 'Skip for now',
  'onboarding.skip.confirm.cancel': 'Continue setup',
  'onboarding.customizing.progress.label': 'Onboarding progress',
  'onboarding.customizing.title': 'Setting up your workspace',
  'onboarding.customizing.title.done': 'Workspace ready',
  'onboarding.customizing.subtitle':
    'Customizing based on your tools and workflows',
  'onboarding.customizing.subtitle.done': 'Taking you in\u2026',
  'onboarding.customizing.step.analyzing': 'Analyzing your setup',
  'onboarding.customizing.step.connecting': 'Connecting your tools',
  'onboarding.customizing.step.preparing': 'Preparing your workspace',
  'onboarding.customizing.insight.workspace': 'Customizing your workspace',
  'onboarding.customizing.insight.tools':
    'Preparing your tools and workflows',
  'onboarding.customizing.insight.environment': 'Finalizing your environment',
  'onboarding.error.saveFailed': 'We couldn\u2019t save your answers. Please try again.',
  'onboarding.required': 'Required',

  // Step 1 — About you
  'onboarding.aboutYou.title': 'About you',
  'onboarding.aboutYou.subtitle':
    'Help us personalize your workspace and connect you with the right tools.',
  'onboarding.aboutYou.firstName': 'First name',
  'onboarding.aboutYou.firstName.placeholder': 'Jane',
  'onboarding.aboutYou.lastName': 'Last name',
  'onboarding.aboutYou.lastName.placeholder': 'Smith',
  'onboarding.aboutYou.companyName': 'Company name',
  'onboarding.aboutYou.companyName.placeholder': 'Acme Corp',
  'onboarding.aboutYou.role': 'Role',
  'onboarding.aboutYou.role.placeholder': 'Select your role',
  'onboarding.aboutYou.industry': 'Industry',
  'onboarding.aboutYou.industry.placeholder': 'Select industry',
  'onboarding.aboutYou.companySize': 'Company size',
  'onboarding.aboutYou.companySize.placeholder': 'Select size',

  // Step 2 — Intent
  'onboarding.intent.title': 'What do you want to do?',
  'onboarding.intent.subtitle': 'Select all that apply',
  'onboarding.intent.options': 'Intent options',
  'onboarding.intent.other.placeholder': 'What are you trying to do?',

  // Step 3 — Design & engineering tools
  'onboarding.designStack.title': 'Design & engineering tools',
  'onboarding.designStack.subtitle': 'Select the tools you currently use.',
  'onboarding.designStack.cad': 'CAD Software',
  'onboarding.designStack.cae': 'CAE / Simulation',
  'onboarding.designStack.plm': 'PLM',

  // Step 4 — Manufacturing tools
  'onboarding.mfgStack.title': 'Manufacturing tools',
  'onboarding.mfgStack.subtitle':
    'Tell us about your production tools and setup.',
  'onboarding.mfgStack.cam': 'CAM Software',
  'onboarding.mfgStack.setup': 'Manufacturing Setup',
  'onboarding.mfgStack.mes': 'MES',
  'onboarding.chip.other': 'Other',
  'onboarding.chip.other.placeholder': 'Please specify\u2026',
};

export default messages;
